import { useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  IconButton,
  SortableList,
  useToasterActions,
} from '@opositandopn/ui';
import classnames from 'classnames';
import { IoLink, IoReaderOutline, IoUnlink } from 'react-icons/io5';

import { ResourceDialog } from '../../../../components/shared/resource-dialog/ResourceDialog';
import { BaseComponentProps } from '../../../../types/base-component.types';
import {
  MultipleChoiceQuestionViewModel,
  useUpdateQuestionsStandalone,
} from '../../../questions/api/useMultipleChoiceQuestions';
import { MultipleChoiceQuestionView } from '../../../questions/components/multiple-choice-question-view/MultipleChoiceQuestionView';
import { useUpdateMultipleChoiceAssessmentQuestion } from '../../api/useMultipleChoiceAssessments';

import styles from './MultipleChoiceAssessmentQuestionsPreviewList.module.scss';

type ContainerProps = BaseComponentProps & {
  subjectId: string;
  assessmentId: string;
  questions: MultipleChoiceQuestionViewModel[];
  open?: boolean;
  onClose: () => void;
};

function MultipleChoiceAssessmentQuestionsPreviewList({
  className,
  subjectId,
  assessmentId,
  questions,
  open,
  onClose,
  testId = 'ui-multiple-choice-assessment-questions-preview-list',
}: ContainerProps) {
  const { sendMessage } = useToasterActions();
  const [current, setCurrent] = useState<string>();

  const [items, setItems] = useState(
    questions.map((question) => ({
      id: question.id,
    })),
  );

  const { mutate: updateAssessmentQuestion } =
    useUpdateMultipleChoiceAssessmentQuestion();

  const { mutate: updateQuestionsStandalone, isPending: isUpdatingStandalone } =
    useUpdateQuestionsStandalone();

  const anyStandalone = questions.some((q) => q.standalone);
  const questionIds = questions.map((q) => q.id);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className={classnames(styles['card-popup'], className)}
        data-testid={testId}
      >
        <h4>Listado de preguntas</h4>
        <div className={styles['list']}>
          <SortableList
            className={styles['sortable-list']}
            items={items}
            onChange={(id, _from, to, newItems) => {
              setItems(newItems);
              updateAssessmentQuestion({
                subjectId,
                assessmentId,
                questionId: id.toString(),
                index: to,
              });
            }}
            renderItem={(item) => (
              <SortableList.Item
                className={styles['sortable-list__item']}
                id={item.id}
              >
                <div className={styles['sortable-item-content']}>
                  {questions.find((question) => question.id === item.id)?.text}
                  <IconButton
                    icon={<IoReaderOutline />}
                    size="sm"
                    onClick={() => {
                      setCurrent(item.id);
                    }}
                  />
                </div>
              </SortableList.Item>
            )}
          />
        </div>
        <Button
          className={styles['update-standalone']}
          variant="outline-primary"
          suffixIcon={anyStandalone ? <IoLink /> : <IoUnlink />}
          size="sm"
          onClick={() => {
            updateQuestionsStandalone(
              {
                subjectId,
                questionIds,
                standalone: !anyStandalone,
              },
              {
                onSuccess: () => {
                  sendMessage('¡Hecho!', 'Preguntas actualizadas.', 'success');
                },
              },
            );
          }}
          loading={isUpdatingStandalone}
          disabled={isUpdatingStandalone || questionIds.length === 0}
        >
          {anyStandalone
            ? 'Marcar como dependientes'
            : 'Marcar como independientes'}
        </Button>
        <ResourceDialog open={current !== undefined}>
          {current && (
            <MultipleChoiceQuestionView
              subjectId={subjectId}
              questionId={current}
              onClose={() => {
                setCurrent(undefined);
              }}
            />
          )}
        </ResourceDialog>
      </DialogContent>
    </Dialog>
  );
}

export { MultipleChoiceAssessmentQuestionsPreviewList };
